import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/AppShell";
import GlobalShell from "@/components/GlobalShell";
import { canSeeAccounts, canSeeHiring, canSeeNurture, canSeeSalesCheck } from "@/components/GlobalSwitcher";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import Recordings from "@/pages/Recordings";
import RecordingDetail from "@/pages/RecordingDetail";
import Scripts from "@/pages/Scripts";
import Sales from "@/pages/Sales";
import SalesLog from "@/pages/SalesLog";
import FEMonthly from "@/pages/FEMonthly";
import FEConversions from "@/pages/FEConversions";
import Accountability from "@/pages/Accountability";
import QCChecks from "@/pages/QCChecks";
import Retention from "@/pages/Retention";
import Events from "@/pages/Events";
import CheckIn from "@/pages/CheckIn";
import Gate from "@/pages/Gate";
import NoShowFollowUps from "@/pages/NoShowFollowUps";
import Customers from "@/pages/Customers";
import Incentives from "@/pages/Incentives";
import Settings from "@/pages/Settings";
import Expenses from "@/pages/Expenses";
import MerchStock from "@/pages/MerchStock";
import Leads from "@/pages/Leads";
import LeadNurture from "@/pages/LeadNurture";
import Accounts from "@/pages/Accounts";
import SalesCheck from "@/pages/SalesCheck";
import Requests from "@/pages/Requests";
import PettyCash from "@/pages/PettyCash";
import UserManagement from "@/pages/UserManagement";
import BulkImport from "@/pages/BulkImport";
import AuditLog from "@/pages/AuditLog";
import GlobalAdmin from "@/pages/GlobalAdmin";
import Login from "@/pages/Login";
import ExerciseLibrary from "@/pages/ExerciseLibrary";
import PtProgrammes from "@/pages/PtProgrammes";
import GymClosures from "@/pages/GymClosures";
import MembershipPauses from "@/pages/MembershipPauses";
import MemberReferrals from "@/pages/MemberReferrals";
import MemberRewards from "@/pages/MemberRewards";
import MonthlyChallenges from "@/pages/MonthlyChallenges";
import MemberBingo from "@/pages/MemberBingo";
import BingoAdmin from "@/pages/BingoAdmin";
import PtProgrammeBuilder from "@/pages/PtProgrammeBuilder";
import MemberAppClients from "@/pages/MemberAppClients";
import MemberAppInbox from "@/pages/MemberAppInbox";
import PlanReviews from "@/pages/PlanReviews";
import MemberAppAnnouncements from "@/pages/MemberAppAnnouncements";
import RenewalReminders from "@/pages/RenewalReminders";
import GhlConnections from "@/pages/GhlConnections";
import PaymentAccounts from "@/pages/PaymentAccounts";
import AppRenewals from "@/pages/AppRenewals";
import CashPayments from "@/pages/CashPayments";
import PaymentPlans from "@/pages/PaymentPlans";
import RewardPoints from "@/pages/RewardPoints";
import RentFund from "@/pages/RentFund";
import Improvement from "@/pages/Improvement";
import StaffAttendance from "@/pages/StaffAttendance";
import WorkDeskQr from "@/pages/WorkDeskQr";
import Leave from "@/pages/Leave";
import Roster from "@/pages/Roster";
import AtRisk from "@/pages/AtRisk";
import Upsell from "@/pages/Upsell";
import TrialWeek from "@/pages/TrialWeek";
import UruPass from "@/pages/UruPass";
import UruPasses from "@/pages/UruPasses";
import TrialSignup from "@/pages/TrialSignup";
import NutritionWebinar from "@/pages/NutritionWebinar";
import GymNumbers from "@/pages/GymNumbers";
import AskData from "@/pages/AskData";
import AdSpend from "@/pages/AdSpend";
import Systems from "@/pages/Systems";
import Board from "@/pages/Board";
import WalkinForm from "@/pages/WalkinForm";
import Careers from "@/pages/Careers";
import CareersBook from "@/pages/CareersBook";
import Hiring from "@/pages/Hiring";
import InvestorRentFund from "@/pages/InvestorRentFund";
import ProtectedRoute from "@/components/ProtectedRoute";
import { AuthProvider } from "@/context/AuthContext";

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Public, read-only investor link — no login */}
      <Route path="/investor/:token" element={<InvestorRentFund />} />
      <Route path="/start/:slug" element={<WalkinForm />} />
      {/* Members play LIFTR BINGO here with their app sign-in (no staff login) */}
      <Route path="/bingo" element={<MemberBingo />} />
      {/* 1 Week Trial: the public page ads link to (no login) */}
      <Route path="/try/:gym" element={<TrialSignup />} />
      {/* URU Games 2-week pass: each participant's claim link (no login) */}
      <Route path="/uru/:code" element={<UruPass />} />
      {/* Corporate nutrition webinar: the page HR teams share with their staff (no login) */}
      <Route path="/webinar" element={<NutritionWebinar />} />
      <Route path="/webinar/nutrition" element={<NutritionWebinar />} />
      {/* Public coach applications + interview booking (no login) */}
      <Route path="/careers" element={<Careers />} />
      <Route path="/careers/book/:token" element={<CareersBook />} />
      {/* Lead Nurture: a full-page cross-gym view with its own top bar (no left nav). */}
      <Route
        path="/lead-nurture"
        element={
          <ProtectedRoute>
            <GlobalShell canSee={canSeeNurture} testid="nurture">
              <LeadNurture />
            </GlobalShell>
          </ProtectedRoute>
        }
      />
      {/* Accounts: the accounts team's cross-gym view, same full-page shell. */}
      <Route
        path="/accounts"
        element={
          <ProtectedRoute>
            <GlobalShell canSee={canSeeAccounts} testid="accounts">
              <Accounts />
            </GlobalShell>
          </ProtectedRoute>
        }
      />
      {/* Continuous Improvement: every team's training and practice, one cross-gym view. */}
      <Route
        path="/improvement"
        element={
          <ProtectedRoute>
            <GlobalShell testid="improvement">
              <Improvement />
            </GlobalShell>
          </ProtectedRoute>
        }
      />
      {/* Hiring: coach applications to hire, managers (their gyms) and admins. */}
      <Route
        path="/hiring"
        element={
          <ProtectedRoute>
            <GlobalShell canSee={canSeeHiring} testid="hiring">
              <Hiring />
            </GlobalShell>
          </ProtectedRoute>
        }
      />
      {/* Ad spend: admins, a month's Meta spend split across gyms by their ad leads. */}
      <Route
        path="/ad-spend"
        element={
          <ProtectedRoute>
            <GlobalShell canSee={canSeeSalesCheck} testid="ad-spend">
              <AdSpend />
            </GlobalShell>
          </ProtectedRoute>
        }
      />
      {/* Systems: admins, the daily check of every bot, background job, ad account and paid balance. */}
      <Route
        path="/systems"
        element={
          <ProtectedRoute>
            <GlobalShell canSee={canSeeSalesCheck} testid="systems">
              <Systems />
            </GlobalShell>
          </ProtectedRoute>
        }
      />
      {/* Sales check: admins, questionnaire / GoHighLevel shows / recordings across every gym. */}
      <Route
        path="/sales-check"
        element={
          <ProtectedRoute>
            <GlobalShell canSee={canSeeSalesCheck} testid="sales-check">
              <SalesCheck />
            </GlobalShell>
          </ProtectedRoute>
        }
      />
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        {/* Dashboard — visible to everyone (Feb 2026): every role can see the reports */}
        <Route path="/" element={<Dashboard />} />
        <Route path="/profile" element={<Profile />} />

        {/* Data entry — available to every authenticated user */}
        <Route path="/sales" element={<Sales />} />
        <Route path="/sales/dashboard" element={<FEMonthly />} />
        <Route path="/sales/fe-conversion" element={<FEConversions />} />
        <Route path="/sales/accountability" element={<Accountability />} />
        <Route path="/sales/qc" element={<QCChecks />} />
        <Route path="/retention" element={<Retention />} />
        <Route path="/at-risk" element={<AtRisk />} />
        <Route path="/upsell" element={<Upsell />} />
        <Route path="/trial-week" element={<TrialWeek />} />
        <Route path="/uru-passes" element={<UruPasses />} />
        <Route path="/numbers" element={<GymNumbers />} />
        <Route path="/ask" element={<AskData />} />
        <Route path="/events" element={<Events />} />
        <Route path="/renewal-reminders" element={<RenewalReminders />} />
        <Route path="/ghl" element={<GhlConnections />} />
        <Route path="/payments" element={<PaymentAccounts />} />
        <Route path="/app-renewals" element={<AppRenewals />} />
        <Route path="/cash-payments" element={<CashPayments />} />
        <Route path="/payment-plans" element={<PaymentPlans />} />
        <Route path="/reward-points" element={<RewardPoints />} />
        <Route path="/check-in" element={<CheckIn />} />
        {/* Petty cash, merch stock and gate entries: managers and up, opened from Settings → Gym */}
        <Route path="/gate" element={<ProtectedRoute minRole="manager"><Gate /></ProtectedRoute>} />
        <Route path="/training" element={<Navigate to="/improvement?tab=sales-training&view=series" replace />} />
        <Route path="/practice" element={<Navigate to="/improvement?tab=sales-training&view=practice" replace />} />
        <Route path="/coach-practice" element={<Navigate to="/improvement?tab=coach" replace />} />
        <Route path="/work" element={<StaffAttendance />} />
        <Route path="/work/desk" element={<WorkDeskQr />} />
        <Route path="/leave" element={<Leave />} />
        <Route path="/roster" element={<Roster />} />
        <Route path="/board" element={<Board />} />
        <Route path="/no-shows" element={<NoShowFollowUps />} />
        <Route path="/sales-log" element={<SalesLog />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/expenses" element={<Expenses />} />
        <Route path="/requests" element={<Requests />} />
        <Route path="/petty-cash" element={<ProtectedRoute minRole="manager"><PettyCash /></ProtectedRoute>} />
        <Route path="/merch-stock" element={<ProtectedRoute minRole="manager"><MerchStock /></ProtectedRoute>} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/member-app/wod" element={<Navigate to="/member-app/programmes" replace />} />
        <Route path="/member-app/exercises" element={<ExerciseLibrary />} />
        <Route path="/member-app/programmes" element={<PtProgrammes />} />
        <Route path="/member-app/closures" element={<GymClosures />} />
        <Route path="/member-app/pauses" element={<MembershipPauses />} />
        <Route path="/member-app/coach-scores" element={<Navigate to="/sales/accountability" replace />} />
        <Route path="/member-app/referrals" element={<MemberReferrals />} />
        <Route path="/member-app/rewards" element={<MemberRewards />} />
        <Route path="/member-app/challenges" element={<MonthlyChallenges />} />
        <Route path="/member-app/bingo" element={<BingoAdmin />} />
        <Route path="/member-app/programmes/new" element={<PtProgrammeBuilder />} />
        <Route path="/member-app/programmes/:id" element={<PtProgrammeBuilder />} />
        <Route path="/member-app/clients" element={<MemberAppClients />} />
        <Route path="/member-app/inbox" element={<MemberAppInbox />} />
        <Route path="/member-app/plan-reviews" element={<PlanReviews />} />
        <Route path="/member-app/announcements" element={<MemberAppAnnouncements />} />

        {/* Incentives & slabs — visible to everyone (view-only for non-master-admin) */}
        <Route path="/incentives" element={<Incentives />} />

        {/* Manager+ */}
        <Route
          path="/recordings"
          element={
            <ProtectedRoute minRole="manager">
              <Recordings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/recordings/:id"
          element={
            <ProtectedRoute minRole="user">
              <RecordingDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rent-fund"
          element={
            <ProtectedRoute minRole="manager">
              <RentFund />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute minRole="manager">
              <UserManagement />
            </ProtectedRoute>
          }
        />

        {/* Global Admin: admin+ can view + assign QC / Lead Nurture teams; only master_admin can create facilities & promote roles. */}
        <Route
          path="/global-admin"
          element={
            <ProtectedRoute minRole="admin">
              <GlobalAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute minRole="manager">
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scripts"
          element={
            <ProtectedRoute minRole="admin">
              <Scripts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bulk-import"
          element={
            <ProtectedRoute minRole="manager">
              <BulkImport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit-log"
          element={
            <ProtectedRoute minRole="manager">
              <AuditLog />
            </ProtectedRoute>
          }
        />

        {/* Legacy redirects */}
        <Route path="/bi" element={<Navigate to="/" replace />} />
        <Route path="/reports" element={<Navigate to="/" replace />} />
        <Route path="/team" element={<Navigate to="/users" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#0A0A0A] text-zinc-100">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster richColors position="top-right" theme="dark" />
    </div>
  );
}

export default App;
